import React, { useState } from 'react'
import { Box, Typography, IconButton, Tooltip, Menu, MenuItem } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import SettingsBackupRestoreIcon from '@mui/icons-material/SettingsBackupRestore'
import { useAppTheme } from '@/theme/ThemeContext'
import MeterInfoIcon from '@/assets/meter_info_icon.svg'

export const SYSTEM_COLUMNS = [
  { id: 'water_meter', label: 'מספר כרטיס מים' },
  { id: 'internal_meter', label: 'מספר מד פנימי' },
  { id: 'reading', label: 'קריאה' },
  { id: 'address', label: 'כתובת נכס' },
  { id: 'phone', label: 'טלפון' },
  { id: 'payer_id', label: 'מספר משלם' },
]

// Mock file headers from the screenshot
const FILE_HEADERS_MOCK = [
  'כרטיס מים',
  'מספר מד',
  'כתובת',
  'קריאה אחרונה',
  'ספיקה',
  'קואורדינטות',
  'דוא"ל לקוח'
]

interface Step2ColumnMappingProps {
  useFileHeaders: boolean
  onUseFileHeadersChange: (val: boolean) => void
  mappings: Record<string, string>
  removedColumns: Set<string>
  onMappingChange: (systemId: string, fileColumn: string) => void
  onRemoveMapping: (systemId: string) => void
  onRestoreMapping: (systemId: string) => void
}

export const Step2ColumnMapping: React.FC<Step2ColumnMappingProps> = ({
  useFileHeaders,
  onUseFileHeadersChange,
  mappings,
  removedColumns,
  onMappingChange,
  onRemoveMapping,
  onRestoreMapping
}) => {
  const { mode } = useAppTheme()
  const isDarkMode = mode === 'dark'
  
  const TEXT_PRIMARY = isDarkMode ? '#BBD7FF' : '#003F61'
  const TEXT_SECONDARY = isDarkMode ? '#BBD7FF' : '#64748B'

  // Dropdown state
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [activeColId, setActiveColId] = useState<string | null>(null)

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>, colId: string) => {
    setAnchorEl(event.currentTarget)
    setActiveColId(colId)
  }

  const handleCloseMenu = () => {
    setAnchorEl(null)
    setActiveColId(null)
  }

  const handleSelectHeader = (header: string) => {
    if (activeColId) {
      onMappingChange(activeColId, header)
    }
    handleCloseMenu()
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, flex: 1, overflow: 'hidden' }}>
      <Box sx={{ textAlign: 'right', px: 1 }}>
        <Typography sx={{ color: TEXT_PRIMARY, fontWeight: 500, fontSize: '15px', mb: 1, fontFamily: 'Rubik' }}>
          כל העמודות שמופיעות ברשימה יתעדכנו במערכת לפי המידע בקובץ החדש. ניתן למחוק עמודות על מנת למנוע עדכון נתונים מהן.
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: 1 }}>
         <Box sx={{ 
           width: '40px', 
           height: '20px', 
           bgcolor: useFileHeaders ? '#3B82F6' : '#E2E8F0', 
           borderRadius: '20px', 
           position: 'relative', 
           cursor: 'pointer',
           transition: 'all 0.2s'
         }} onClick={() => onUseFileHeadersChange(!useFileHeaders)}>
           <Box sx={{ 
             width: '16px', 
             height: '16px', 
             bgcolor: '#FFFFFF', 
             borderRadius: '50%', 
             position: 'absolute', 
             top: '2px', 
             left: useFileHeaders ? '22px' : '2px',
             transition: 'all 0.2s'
           }} />
         </Box>
         <Typography sx={{ color: TEXT_SECONDARY, fontSize: '14px', fontFamily: 'Rubik' }}>שימוש בכותרות מהקובץ</Typography>
      </Box>

      <Box sx={{ 
        flex: 1, 
        overflowY: 'auto', 
        pr: 2,
        '&::-webkit-scrollbar': { width: '4px' },
        '&::-webkit-scrollbar-thumb': { bgcolor: isDarkMode ? '#2A364F' : '#E2E8F0', borderRadius: '4px' }
      }}>
        <Box sx={{ maxWidth: '400px', mx: 'auto', width: '100%' }}>
          {/* Table Headers */}
          <Box sx={{ display: 'flex', borderBottom: `1px solid ${isDarkMode ? '#2A364F' : '#E2E8F0'}`, pb: 1, mb: 2 }}>
            <Typography sx={{ flex: 1, textAlign: 'right', color: isDarkMode ? '#72A4EB' : '#3B82F6', fontWeight: 600, fontSize: '14px', fontFamily: 'Rubik' }}>
              כותרת בקובץ
            </Typography>
            <Typography sx={{ width: '150px', textAlign: 'right', color: isDarkMode ? '#72A4EB' : '#3B82F6', fontWeight: 600, fontSize: '14px', fontFamily: 'Rubik', pr: 1 }}>
              כותרת במערכת
            </Typography>
            <Box sx={{ width: '40px' }} />
          </Box>

          {/* Table Rows */}
          {SYSTEM_COLUMNS.map((col) => {
            const isRemoved = removedColumns.has(col.id)
            return (
              <Box 
                key={col.id} 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  mb: 1, 
                  position: 'relative',
                  opacity: isRemoved ? 0.4 : 1,
                  transition: 'all 0.2s'
                }}
              >
                {/* Continuous Strike-through Line */}
                {isRemoved && (
                  <Box 
                    sx={{ 
                      position: 'absolute',
                      right: '0',
                      left: '40px', 
                      height: '1px',
                      bgcolor: TEXT_PRIMARY,
                      top: '50%',
                      zIndex: 1,
                      pointerEvents: 'none'
                    }} 
                  />
                )}
                
                {/* Right Side: System Column Label */}
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 1 }}>
                  <Tooltip 
                    title="דוגמא מהקובץ: ערך לדוגמא 123" 
                    arrow 
                    placement="right"
                    slotProps={{
                      tooltip: {
                        sx: {
                          bgcolor: '#061F43A1',
                          color: '#F8FAFD',
                          fontSize: '13px',
                          fontFamily: 'Rubik',
                          borderRadius: '8px',
                          padding: '8px 12px',
                          height: '30px',
                          textAlign: 'center',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }
                      },
                      arrow: {
                        sx: {
                          color: '#061F43A1'
                        }
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <img 
                        src={MeterInfoIcon} 
                        alt="Info" 
                        style={{ 
                          width: 16, 
                          height: 16,
                          filter: isDarkMode ? 'brightness(0) invert(1)' : 'none'
                        }} 
                      />
                    </Box>
                  </Tooltip>
                  <Typography 
                    sx={{ 
                      color: TEXT_PRIMARY, 
                      fontSize: '14px', 
                      fontWeight: 600, 
                      fontFamily: 'Rubik'
                    }}
                  >
                    {col.label}
                  </Typography>
                </Box>

                {/* Left Side: Dropdown + Action Button grouped closely */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: '150px' }}>
                    <Box 
                      onClick={(e) => !isRemoved && handleOpenMenu(e, col.id)}
                      sx={{ 
                        border: `1px solid ${isDarkMode ? '#2A364F' : '#E2E8F0'}`, 
                        borderRadius: '4px', 
                        p: '6px 12px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        cursor: isRemoved ? 'default' : 'pointer',
                        bgcolor: isDarkMode ? '#1E293B' : '#FFFFFF',
                        pointerEvents: isRemoved ? 'none' : 'auto'
                      }}
                    >
                      <Typography 
                        sx={{ 
                          color: mappings[col.id] ? TEXT_PRIMARY : TEXT_SECONDARY, 
                          fontSize: '13px', 
                          fontFamily: 'Rubik',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {mappings[col.id] || 'בחירת עמודה'}
                      </Typography>
                      <KeyboardArrowDownIcon sx={{ color: '#94A3B8', fontSize: 18 }} />
                    </Box>
                  </Box>

                  <Tooltip title={isRemoved ? "שחזור שדה" : "מחיקת שדה"} arrow placement="top">
                    <IconButton 
                      size="small" 
                      onClick={() => isRemoved ? onRestoreMapping(col.id) : onRemoveMapping(col.id)} 
                      sx={{ 
                        color: '#94A3B8', 
                        width: '40px',
                        zIndex: 2,
                        '&:hover': {
                          color: isRemoved ? '#3B82F6' : '#EF4444',
                          bgcolor: isRemoved ? 'rgba(59, 130, 246, 0.1)' : 'rgba(239, 68, 68, 0.1)'
                        }
                      }}
                    >
                      {isRemoved ? <SettingsBackupRestoreIcon sx={{ fontSize: 18 }} /> : <CloseIcon sx={{ fontSize: 18 }} />}
                    </IconButton>
                  </Tooltip>
                </Box>
                
              </Box>
            )
          })}
        </Box>
      </Box>

      {/* The Actual Dropdown Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        PaperProps={{
          sx: {
            mt: 1,
            boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.08)',
            borderRadius: '12px',
            minWidth: '150px',
            '& .MuiList-root': { padding: '4px' }
          }
        }}
      >
        {FILE_HEADERS_MOCK.map((header) => (
          <MenuItem 
            key={header} 
            onClick={() => handleSelectHeader(header)}
            sx={{
              justifyContent: 'center',
              borderRadius: '8px',
              my: 0.2,
              '&:hover': {
                bgcolor: '#EBF5FF',
              },
              '&.Mui-selected': {
                bgcolor: '#EBF5FF',
                '&:hover': { bgcolor: '#EBF5FF' }
              }
            }}
          >
            <Typography 
              sx={{ 
                color: '#003F61', 
                fontWeight: 600, 
                fontSize: '14px', 
                fontFamily: 'Rubik',
                textAlign: 'center'
              }}
            >
              {header}
            </Typography>
          </MenuItem>
        ))}
      </Menu>
    </Box>
  )
}
